// src/feedbackAdmin.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import QRSubmissions from './QRSubmissions';

// ============================================
// API
// ============================================
const API_URL =
  process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

const APP_NAME =
  process.env.REACT_APP_APP_NAME || 'QR Feedback System';

// ============================================
// ADMIN
// ============================================
const Admin = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('generate');

  // QR
  const [officeName, setOfficeName] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrHistory, setQrHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Modals
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedQR, setSelectedQR] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  // Submissions
  const [showSubmissions, setShowSubmissions] = useState(false);
  const [selectedQRForSubmissions, setSelectedQRForSubmissions] =
    useState(null);
  const [submissionsSearchTerm, setSubmissionsSearchTerm] = useState('');

  // Form Builder
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const [fieldType, setFieldType] = useState('text');
  const [fieldLabel, setFieldLabel] = useState('');
  const [fieldRequired, setFieldRequired] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);

  // Options for Radio, Checkbox, Select
  const [fieldOptions, setFieldOptions] = useState(['Option 1', 'Option 2']);
  const [newOption, setNewOption] = useState('');

  // Rating
  const [ratingThreshold, setRatingThreshold] = useState(3);
  const [descriptionRequired, setDescriptionRequired] = useState(true);

  // ============================================
  // LOAD USER + QR HISTORY
  // ============================================
  useEffect(() => {
    const userData = localStorage.getItem('user');

    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {
        console.error('Invalid user data:', error);
      }
    }

    loadQRHistory();

    console.log('📡 API URL:', API_URL);
    console.log(
      '🔑 Token exists:',
      !!localStorage.getItem('token')
    );
  }, []);

  // ============================================
  // RESET OPTIONS WHEN FIELD TYPE CHANGES
  // ============================================
  useEffect(() => {
    // Reset options when switching away from option-based fields
    if (fieldType !== 'radio' && fieldType !== 'checkbox' && fieldType !== 'select') {
      setFieldOptions(['Option 1', 'Option 2']);
      setNewOption('');
    }
  }, [fieldType]);

  // ============================================
  // FILTER HISTORY
  // ============================================
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      setFilteredHistory(qrHistory);
      return;
    }

    const filtered = qrHistory.filter((qr) => {
      return (
        String(qr.office_name || '')
          .toLowerCase()
          .includes(term) ||
        String(qr.id || '')
          .toLowerCase()
          .includes(term) ||
        String(qr.username || '')
          .toLowerCase()
          .includes(term)
      );
    });

    setFilteredHistory(filtered);
  }, [searchTerm, qrHistory]);

  // ============================================
  // FILTER SUBMISSIONS QR
  // ============================================
  const filteredSubmissionsQRs = qrHistory.filter((qr) => {
    if (!submissionsSearchTerm.trim()) return true;

    const term = submissionsSearchTerm.toLowerCase();

    return (
      String(qr.office_name || '')
        .toLowerCase()
        .includes(term) ||
      String(qr.id || '')
        .toLowerCase()
        .includes(term)
    );
  });

  // ============================================
  // LOAD QR HISTORY
  // ============================================
  const loadQRHistory = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        console.error('No token found');
        return;
      }

      const url = `${API_URL}/api/qr/all`;

      console.log('📡 Fetching QR history from:', url);

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      console.log('✅ QR History loaded:', data);

      if (data.success) {
        const list = data.data || [];

        setQrHistory(list);
        setFilteredHistory(list);
      } else {
        console.error(
          'Failed to load QR history:',
          data.message
        );
      }
    } catch (error) {
      console.error(
        '❌ Error loading QR history:',
        error
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // VIEW SUBMISSIONS
  // ============================================
  const viewSubmissions = (qr) => {
    setSelectedQRForSubmissions(qr);
    setShowSubmissions(true);
  };

  // ============================================
  // BACK FROM SUBMISSIONS
  // ============================================
  const handleBackFromSubmissions = () => {
    setShowSubmissions(false);
    setSelectedQRForSubmissions(null);
  };

  // ============================================
  // GENERATE QR - WITH DEBUG LOGS
  // ============================================
  const generateQR = async () => {
    if (!officeName.trim()) {
      alert('Please enter office name');
      return;
    }

    // DEBUG: Log all form fields before sending
    console.log('🔍 === FORM FIELDS DEBUG ===');
    console.log('Total fields:', formFields.length);
    formFields.forEach((field, index) => {
      console.log(`📋 Field ${index + 1}:`, {
        id: field.id,
        label: field.label,
        type: field.type,
        required: field.required,
        options: field.options,
        optionsType: typeof field.options,
        isArray: Array.isArray(field.options),
        optionsLength: Array.isArray(field.options) ? field.options.length : 'N/A'
      });
    });
    console.log('🔍 ========================');

    // Check if any option-based field is missing options
    const fieldsWithoutOptions = formFields.filter(
      f => (f.type === 'radio' || f.type === 'checkbox' || f.type === 'select') && 
            (!f.options || f.options.length === 0)
    );
    
    if (fieldsWithoutOptions.length > 0) {
      console.warn('⚠️ Fields without options:', fieldsWithoutOptions);
      alert('Some fields are missing options! Please check and add options.');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        alert('Please login again');
        setLoading(false);
        return;
      }

      // Get next QR number
      const nextNumberResponse = await fetch(
        `${API_URL}/api/qr/next-number`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      let qrNumber = '';

      if (nextNumberResponse.ok) {
        const data = await nextNumberResponse.json();

        qrNumber =
          data.qrNumber ||
          `QR-${Date.now().toString().slice(-6)}`;
      } else {
        qrNumber = `QR-${Date.now().toString().slice(-6)}`;
      }

      // QR URL
      const frontendUrl = window.location.origin;

      const qrContent = `${frontendUrl}/qr/${qrNumber}`;

      console.log('🔗 QR URL:', qrContent);

      // Generate image
      const qrCodeDataURL = await QRCode.toDataURL(
        qrContent,
        {
          width: 300,
          margin: 2,
          color: {
            dark: '#1a1a2e',
            light: '#ffffff'
          }
        }
      );

      setQrCodeUrl(qrCodeDataURL);

      // Prepare request body with full data
      const requestBody = {
        officeName,
        qrImage: qrCodeDataURL,
        formFields: formFields,
        ratingConfig: {
          threshold: ratingThreshold,
          descriptionRequired
        }
      };

      console.log('📤 Sending request body:', JSON.stringify(requestBody, null, 2));

      // Save
      const response = await fetch(
        `${API_URL}/api/qr/save`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      const data = await response.json();

      if (data.success) {
        await loadQRHistory();

        setShowPrintModal(true);
        setShowFormBuilder(false);
        setFormFields([]);
        setFieldOptions(['Option 1', 'Option 2']);
        setNewOption('');

        alert(
          `✅ QR Code generated! ID: ${
            data.data?.qrNumber || qrNumber
          }`
        );
      } else {
        alert(
          '❌ Failed to save QR code: ' +
            (data.message || 'Unknown error')
        );
      }
    } catch (error) {
      console.error(
        'Error generating QR:',
        error
      );

      alert(
        'Failed to generate QR code: ' +
          error.message
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // DELETE QR
  // ============================================
  const deleteQR = async (id) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this QR code? This will also delete all submitted data.'
      )
    ) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');

      if (!token) {
        alert('Please login again');
        return;
      }

      const response = await fetch(
        `${API_URL}/api/qr/${id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error(
          `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();

      if (data.success) {
        await loadQRHistory();

        setShowQRModal(false);
        setSelectedQR(null);

        alert(
          'QR code deleted successfully!'
        );
      } else {
        alert(
          'Failed to delete QR: ' +
            (data.message || 'Unknown error')
        );
      }
    } catch (error) {
      console.error(
        'Error deleting QR:',
        error
      );

      alert(
        'Error deleting QR code: ' +
          error.message
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // VIEW QR
  // ============================================
  const viewQRDetails = (qr) => {
    setSelectedQR(qr);
    setShowQRModal(true);
  };

  // ============================================
  // DOWNLOAD QR
  // ============================================
  const downloadQR = (qrCode, name) => {
    if (!qrCode) {
      alert('QR image not available');
      return;
    }

    const link = document.createElement('a');

    link.download = `QR_${name}_${new Date()
      .toISOString()
      .split('T')[0]}.png`;

    link.href = qrCode;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ============================================
  // PRINT QR
  // ============================================
  const printQR = async () => {
    const printContent =
      document.getElementById('qr-print-area');

    if (!printContent) return;

    try {
      const canvas = await html2canvas(
        printContent,
        {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff'
        }
      );

      const imgData =
        canvas.toDataURL('image/png');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [400, 500]
      });

      pdf.addImage(
        imgData,
        'PNG',
        20,
        20,
        360,
        460
      );

      pdf.save(
        `QR_${officeName}_${new Date()
          .toISOString()
          .split('T')[0]}.pdf`
      );

      setShowPrintModal(false);
      setOfficeName('');
      setQrCodeUrl('');
    } catch (error) {
      console.error(
        'Print error:',
        error
      );

      alert('Error printing QR code');
    }
  };

  // ============================================
  // ADD OPTION
  // ============================================
  const addOption = () => {
    if (!newOption.trim()) {
      alert('Please enter an option');
      return;
    }

    if (fieldOptions.includes(newOption.trim())) {
      alert('Option already exists');
      return;
    }

    console.log(`➕ Adding option: "${newOption.trim()}"`);
    setFieldOptions([...fieldOptions, newOption.trim()]);
    setNewOption('');
  };

  // ============================================
  // REMOVE OPTION
  // ============================================
  const removeOption = (indexToRemove) => {
    console.log(`❌ Removing option at index ${indexToRemove}`);
    const updated = fieldOptions.filter((_, i) => i !== indexToRemove);
    setFieldOptions(updated);
  };

  // ============================================
  // FORM BUILDER
  // ============================================
  const addField = () => {
    if (!fieldLabel.trim()) {
      alert('Please enter field label');
      return;
    }

    // Validate options for radio, checkbox, select
    if ((fieldType === 'radio' || fieldType === 'checkbox' || fieldType === 'select') && fieldOptions.length < 2) {
      alert('Please add at least 2 options');
      return;
    }

    // DEBUG: Log what we're adding
    console.log('🔍 Adding field with:', {
      label: fieldLabel,
      type: fieldType,
      options: fieldOptions,
      optionsLength: fieldOptions.length
    });

    const newField = {
      id: Date.now().toString(),
      type: fieldType,
      label: fieldLabel,
      required: fieldRequired,
      ratingThreshold:
        fieldType === 'rating'
          ? ratingThreshold
          : null,
      descriptionRequired:
        fieldType === 'rating'
          ? descriptionRequired
          : false,
      options:
        fieldType === 'radio' || fieldType === 'checkbox' || fieldType === 'select'
          ? [...fieldOptions]
          : []
    };

    console.log('✅ New field created:', newField);

    if (editingFieldIndex !== null) {
      const updatedFields = [...formFields];

      updatedFields[editingFieldIndex] =
        newField;

      setFormFields(updatedFields);
      setEditingFieldIndex(null);
    } else {
      setFormFields([
        ...formFields,
        newField
      ]);
    }

    // Reset form
    setFieldLabel('');
    setFieldType('text');
    setFieldRequired(false);
    setRatingThreshold(3);
    setDescriptionRequired(true);
    setFieldOptions(['Option 1', 'Option 2']);
    setNewOption('');

    // Log current formFields after adding
    console.log('📋 Current formFields:', formFields);
  };

  const editField = (index) => {
    const field = formFields[index];

    console.log('✏️ Editing field:', field);

    setFieldLabel(field.label);
    setFieldType(field.type);
    setFieldRequired(field.required);

    if (field.type === 'rating') {
      setRatingThreshold(
        field.ratingThreshold || 3
      );

      setDescriptionRequired(
        field.descriptionRequired !== false
      );
    }

    if (field.type === 'radio' || field.type === 'checkbox' || field.type === 'select') {
      const options = field.options || ['Option 1', 'Option 2'];
      setFieldOptions(options);
      console.log('📋 Loaded options for edit:', options);
    } else {
      setFieldOptions(['Option 1', 'Option 2']);
    }

    setEditingFieldIndex(index);
  };

  const removeField = (index) => {
    const updatedFields =
      formFields.filter(
        (_, i) => i !== index
      );

    setFormFields(updatedFields);

    if (editingFieldIndex === index) {
      setEditingFieldIndex(null);
      setFieldLabel('');
      setFieldType('text');
      setFieldRequired(false);
      setFieldOptions(['Option 1', 'Option 2']);
    }
  };

  const moveFieldUp = (index) => {
    if (index <= 0) return;

    const updatedFields = [...formFields];

    [
      updatedFields[index],
      updatedFields[index - 1]
    ] = [
      updatedFields[index - 1],
      updatedFields[index]
    ];

    setFormFields(updatedFields);
  };

  const moveFieldDown = (index) => {
    if (index >= formFields.length - 1)
      return;

    const updatedFields = [...formFields];

    [
      updatedFields[index],
      updatedFields[index + 1]
    ] = [
      updatedFields[index + 1],
      updatedFields[index]
    ];

    setFormFields(updatedFields);
  };

  // ============================================
  // RENDER OPTIONS INPUT
  // ============================================
  const renderOptionsInput = () => {
    const optionBasedTypes = ['radio', 'checkbox', 'select'];
    
    if (!optionBasedTypes.includes(fieldType)) {
      return null;
    }

    const typeLabel = {
      'radio': 'Radio Options',
      'checkbox': 'Checkbox Options',
      'select': 'Dropdown Options'
    };

    return (
      <div style={styles.optionsContainer}>
        <label style={styles.label}>
          {typeLabel[fieldType] || 'Options'}
        </label>

        <div style={styles.optionsInputRow}>
          <input
            type="text"
            placeholder={`Enter option (e.g., ${fieldType === 'checkbox' ? 'Agree' : 'Male'})`}
            value={newOption}
            onChange={(e) =>
              setNewOption(e.target.value)
            }
            style={{...styles.input, flex: 1}}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addOption();
              }
            }}
          />

          <button
            onClick={addOption}
            style={styles.addOptionBtn}
          >
            Add
          </button>
        </div>

        <div style={styles.optionsList}>
          {fieldOptions.map((opt, idx) => (
            <span key={idx} style={styles.optionTag}>
              {opt}
              <button
                onClick={() => removeOption(idx)}
                style={styles.removeOptionBtn}
              >
                ×
              </button>
            </span>
          ))}
          {fieldOptions.length === 0 && (
            <span style={styles.noOptionsText}>
              No options added yet
            </span>
          )}
        </div>

        {fieldOptions.length > 0 && (
          <div style={styles.optionsHint}>
            💡 {fieldOptions.length} option(s) added
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // RENDER FIELD PREVIEW
  // ============================================
  const renderFieldPreview = (field) => {
    if (field.type === 'rating') {
      return (
        <div style={styles.ratingContainer}>
          <div style={styles.starContainer}>
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                style={{
                  fontSize: '32px',
                  color: '#ddd'
                }}
              >
                ★
              </span>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === 'radio') {
      return (
        <div style={styles.radioGroupPreview}>
          {field.options && field.options.map((opt, idx) => (
            <label key={idx} style={styles.radioLabelPreview}>
              <input
                type="radio"
                name={`preview_${field.id}`}
                value={opt}
                style={styles.radioInputPreview}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <div style={styles.checkboxGroupPreview}>
          {field.options && field.options.map((opt, idx) => (
            <label key={idx} style={styles.checkboxLabelPreview}>
              <input
                type="checkbox"
                value={opt}
                style={styles.checkboxInputPreview}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <select style={styles.previewSelect}>
          <option value="">Select...</option>
          {field.options && field.options.map((opt, idx) => (
            <option key={idx} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.type}
        style={styles.previewInput}
        placeholder={`Enter ${field.label}`}
      />
    );
  };

  // ============================================
  // RENDER FIELD BADGE
  // ============================================
  const renderFieldBadge = (field) => {
    const badges = {
      'radio': '🔘 Radio',
      'checkbox': '☑️ Checkbox',
      'select': '📋 Dropdown'
    };
    return badges[field.type] || field.type;
  };

  // ============================================
  // LOGOUT
  // ============================================
  const handleLogout = () => {
    if (
      window.confirm(
        'Are you sure you want to logout?'
      )
    ) {
      localStorage.removeItem('token');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('user');
      localStorage.removeItem('userRole');
      localStorage.removeItem('userName');
      localStorage.removeItem('userMobile');

      navigate('/feedback-login');
    }
  };

  // ============================================
  // CLEAR SEARCH
  // ============================================
  const clearSearch = () => {
    setSearchTerm('');
  };

  const clearSubmissionsSearch = () => {
    setSubmissionsSearchTerm('');
  };

  // ============================================
  // SUBMISSIONS SCREEN
  // ============================================
  if (
    showSubmissions &&
    selectedQRForSubmissions
  ) {
    return (
      <QRSubmissions
        qrList={[selectedQRForSubmissions]}
        onBack={handleBackFromSubmissions}
        autoSelectFirst={true}
      />
    );
  }

  // ============================================
  // TOTAL SUBMISSIONS
  // ============================================
  const totalSubmissions =
    qrHistory.reduce(
      (sum, qr) =>
        sum +
        (Number(qr.total_submissions) || 0),
      0
    );

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={styles.container}>

      {/* ========================================
          HEADER
      ======================================== */}
      <header style={styles.header}>
        <h1 style={styles.headerTitle}>
          <span style={styles.headerIcon}>
            📋
          </span>

          {APP_NAME}
        </h1>

        <div style={styles.headerRight}>
          <span style={styles.userInfo}>
            👤 {user?.username || 'Admin'}
          </span>

          <button
            onClick={handleLogout}
            style={styles.logoutBtn}
          >
            🚪 Logout
          </button>
        </div>
      </header>

      <div style={styles.content}>

        {/* ======================================
            TABS
        ====================================== */}
        <div style={styles.tabContainer}>

          <button
            style={{
              ...styles.tabButton,
              ...(activeTab === 'generate'
                ? styles.activeTab
                : {})
            }}
            onClick={() =>
              setActiveTab('generate')
            }
          >
            ➕ Generate QR
          </button>

          <button
            style={{
              ...styles.tabButton,
              ...(activeTab === 'history'
                ? styles.activeTab
                : {})
            }}
            onClick={() =>
              setActiveTab('history')
            }
          >
            📋 History ({filteredHistory.length})
          </button>

          <button
            style={{
              ...styles.tabButton,
              ...(activeTab === 'submissions'
                ? styles.activeTab
                : {})
            }}
            onClick={() =>
              setActiveTab('submissions')
            }
          >
            📊 Submissions ({totalSubmissions})
          </button>

        </div>

        {/* ======================================
            GENERATE TAB
        ====================================== */}
        {activeTab === 'generate' && (
          <div style={styles.tabContent}>

            <div style={styles.generateCard}>

              <h2 style={styles.sectionTitle}>
                Generate New QR Code
              </h2>

              <div style={styles.inputGroup}>
                <label style={styles.label}>
                  Office / Place Name
                </label>

                <input
                  type="text"
                  placeholder="Enter office or place name"
                  value={officeName}
                  onChange={(e) =>
                    setOfficeName(e.target.value)
                  }
                  style={styles.input}
                />
              </div>

              <button
                onClick={() =>
                  setShowFormBuilder(
                    !showFormBuilder
                  )
                }
                style={{
                  ...styles.formBuilderBtn,
                  ...(showFormBuilder
                    ? styles.formBuilderActive
                    : {})
                }}
              >
                {showFormBuilder
                  ? '🔒 Close Form Builder'
                  : '📝 Add Form to QR'}
              </button>

              {/* FORM BUILDER */}
              {showFormBuilder && (
                <div style={styles.formBuilder}>

                  <h3
                    style={
                      styles.formBuilderTitle
                    }
                  >
                    Build Your Form
                  </h3>

                  <div
                    style={
                      styles.addFieldForm
                    }
                  >

                    <div style={styles.fieldRow}>

                      <div
                        style={
                          styles.fieldGroup
                        }
                      >
                        <label
                          style={
                            styles.label
                          }
                        >
                          Field Label
                        </label>

                        <input
                          type="text"
                          placeholder="e.g., Name, Email, Feedback"
                          value={fieldLabel}
                          onChange={(e) =>
                            setFieldLabel(
                              e.target.value
                            )
                          }
                          style={
                            styles.input
                          }
                        />
                      </div>

                      <div
                        style={
                          styles.fieldGroup
                        }
                      >
                        <label
                          style={
                            styles.label
                          }
                        >
                          Field Type
                        </label>

                        <select
                          value={fieldType}
                          onChange={(e) =>
                            setFieldType(
                              e.target.value
                            )
                          }
                          style={
                            styles.select
                          }
                        >
                          <option value="text">
                            Text
                          </option>

                          <option value="email">
                            Email
                          </option>

                          <option value="number">
                            Number
                          </option>

                          <option value="date">
                            Date
                          </option>

                          <option value="rating">
                            ⭐ Star Rating
                          </option>

                          <option value="radio">
                            🔘 Radio Button
                          </option>

                          <option value="checkbox">
                            ☑️ Checkbox
                          </option>

                          <option value="select">
                            📋 Dropdown
                          </option>
                        </select>
                      </div>

                    </div>

                    {/* OPTIONS INPUT - Radio, Checkbox, Select */}
                    {renderOptionsInput()}

                    <div style={styles.fieldRow}>

                      <div
                        style={
                          styles.fieldGroup
                        }
                      >
                        <label
                          style={
                            styles.label
                          }
                        >
                          <input
                            type="checkbox"
                            checked={
                              fieldRequired
                            }
                            onChange={(e) =>
                              setFieldRequired(
                                e.target.checked
                              )
                            }
                            style={
                              styles.checkbox
                            }
                          />

                          Required Field
                        </label>
                      </div>

                      <div
                        style={
                          styles.fieldGroup
                        }
                      >
                        <button
                          onClick={addField}
                          style={
                            styles.addFieldBtn
                          }
                          disabled={(fieldType === 'radio' || fieldType === 'checkbox' || fieldType === 'select') && fieldOptions.length < 2}
                        >
                          {editingFieldIndex !==
                          null
                            ? '✅ Update Field'
                            : '➕ Add Field'}
                        </button>
                        {(fieldType === 'radio' || fieldType === 'checkbox' || fieldType === 'select') && fieldOptions.length < 2 && (
                          <div style={styles.warningText}>
                            ⚠️ Add at least 2 options
                          </div>
                        )}
                      </div>

                    </div>

                  </div>

                  {/* FIELDS */}
                  {formFields.length > 0 && (
                    <div
                      style={
                        styles.fieldsList
                      }
                    >

                      <h4
                        style={
                          styles.fieldsTitle
                        }
                      >
                        Form Fields (
                        {formFields.length})
                      </h4>

                      {formFields.map(
                        (field, index) => (
                          <div
                            key={field.id}
                            style={
                              styles.fieldItem
                            }
                          >

                            <div
                              style={
                                styles.fieldItemInfo
                              }
                            >

                              <span
                                style={
                                  styles.fieldTypeBadge
                                }
                              >
                                {renderFieldBadge(field)}
                              </span>

                              <span
                                style={
                                  styles.fieldLabelText
                                }
                              >
                                {field.label}
                              </span>

                              {field.required && (
                                <span
                                  style={
                                    styles.requiredBadge
                                  }
                                >
                                  Required
                                </span>
                              )}

                              {(field.type === 'radio' || field.type === 'checkbox' || field.type === 'select') && field.options && (
                                <span style={styles.optionsBadge}>
                                  {field.options.length} options
                                </span>
                              )}

                            </div>

                            <div
                              style={
                                styles.fieldItemActions
                              }
                            >

                              <button
                                onClick={() =>
                                  moveFieldUp(
                                    index
                                  )
                                }
                                style={
                                  styles.moveBtn
                                }
                                disabled={
                                  index === 0
                                }
                              >
                                ↑
                              </button>

                              <button
                                onClick={() =>
                                  moveFieldDown(
                                    index
                                  )
                                }
                                style={
                                  styles.moveBtn
                                }
                                disabled={
                                  index ===
                                  formFields.length -
                                    1
                                }
                              >
                                ↓
                              </button>

                              <button
                                onClick={() =>
                                  editField(
                                    index
                                  )
                                }
                                style={
                                  styles.editBtn
                                }
                              >
                                ✏️
                              </button>

                              <button
                                onClick={() =>
                                  removeField(
                                    index
                                  )
                                }
                                style={
                                  styles.removeFieldBtn
                                }
                              >
                                ✖
                              </button>

                            </div>

                          </div>
                        )
                      )}

                    </div>
                  )}

                  {/* PREVIEW */}
                  {formFields.length > 0 && (
                    <div
                      style={
                        styles.previewSection
                      }
                    >

                      <h4
                        style={
                          styles.previewTitle
                        }
                      >
                        Form Preview
                      </h4>

                      <div
                        style={
                          styles.previewForm
                        }
                      >

                        {formFields.map(
                          (field) => (
                            <div
                              key={field.id}
                              style={
                                styles.previewField
                              }
                            >

                              <label
                                style={
                                  styles.previewLabel
                                }
                              >
                                {field.label}

                                {field.required && (
                                  <span
                                    style={
                                      styles.requiredStar
                                    }
                                  >
                                    *
                                  </span>
                                )}
                              </label>

                              {renderFieldPreview(field)}

                            </div>
                          )
                        )}

                      </div>
                    </div>
                  )}

                </div>
              )}

              <button
                onClick={generateQR}
                disabled={loading}
                style={{
                  ...styles.generateBtn,
                  ...(loading
                    ? styles.buttonDisabled
                    : {})
                }}
              >
                {loading
                  ? '⏳ Generating...'
                  : '🚀 Generate QR Code'}
              </button>

            </div>
          </div>
        )}

        {/* ======================================
            HISTORY TAB - Same as before
        ====================================== */}
        {activeTab === 'history' && (
          <div style={styles.tabContent}>

            <div style={styles.historyCard}>

              <div
                style={
                  styles.historyHeader
                }
              >
                <div>
                  <h2
                    style={
                      styles.sectionTitle
                    }
                  >
                    QR Code History
                  </h2>

                  <p
                    style={
                      styles.historySubtitle
                    }
                  >
                    Each row represents one QR
                    code
                  </p>
                </div>

                <span
                  style={
                    styles.totalBadge
                  }
                >
                  Total QR: {
                    filteredHistory.length
                  }
                </span>
              </div>

              {/* SEARCH */}
              <div
                style={
                  styles.searchContainer
                }
              >
                <div
                  style={
                    styles.searchWrapper
                  }
                >
                  <span
                    style={
                      styles.searchIcon
                    }
                  >
                    🔍
                  </span>

                  <input
                    type="text"
                    placeholder="Search by office name, QR ID or creator..."
                    value={searchTerm}
                    onChange={(e) =>
                      setSearchTerm(
                        e.target.value
                      )
                    }
                    style={
                      styles.searchInput
                    }
                  />

                  {searchTerm && (
                    <button
                      onClick={
                        clearSearch
                      }
                      style={
                        styles.clearSearchBtn
                      }
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* LOADING */}
              {loading ? (
                <div
                  style={
                    styles.loadingContainer
                  }
                >
                  <div
                    style={
                      styles.spinner
                    }
                  />

                  <p
                    style={
                      styles.loadingText
                    }
                  >
                    Loading QR Codes...
                  </p>
                </div>
              ) : filteredHistory.length ===
                0 ? (
                <div
                  style={
                    styles.emptyState
                  }
                >
                  <div
                    style={
                      styles.emptyIcon
                    }
                  >
                    📭
                  </div>

                  <p
                    style={
                      styles.emptyMessage
                    }
                  >
                    {searchTerm
                      ? 'No matching QR codes found.'
                      : 'No QR codes generated yet.'}
                  </p>
                </div>
              ) : (
                <div
                  style={
                    styles.historyTableWrap
                  }
                >

                  <table
                    style={
                      styles.historyTable
                    }
                  >

                    <thead>
                      <tr>

                        <th
                          style={
                            styles.historyTh
                          }
                        >
                          #
                        </th>

                        <th
                          style={
                            styles.historyTh
                          }
                        >
                          QR Code
                        </th>

                        <th
                          style={
                            styles.historyTh
                          }
                        >
                          Office / Place
                        </th>

                        <th
                          style={
                            styles.historyTh
                          }
                        >
                          Created Date
                        </th>

                        <th
                          style={
                            styles.historyTh
                          }
                        >
                          Created By
                        </th>

                        <th
                          style={
                            styles.historyTh
                          }
                        >
                          Form Fields
                        </th>

                        <th
                          style={
                            styles.historyTh
                          }
                        >
                          Submissions
                        </th>

                        <th
                          style={
                            styles.historyTh
                          }
                        >
                          Actions
                        </th>

                      </tr>
                    </thead>

                    <tbody>

                      {filteredHistory.map(
                        (qr, index) => {

                          const submissions =
                            Number(
                              qr.total_submissions
                            ) || 0;

                          return (
                            <tr
                              key={qr.id}
                              style={
                                styles.historyTr
                              }
                            >

                              <td
                                style={
                                  styles.historyTd
                                }
                              >
                                <span
                                  style={
                                    styles.rowNumber
                                  }
                                >
                                  {index + 1}
                                </span>
                              </td>

                              <td
                                style={
                                  styles.historyTd
                                }
                              >

                                <div
                                  style={
                                    styles.qrCell
                                  }
                                >

                                  {qr.qr_code ? (
                                    <img
                                      src={
                                        qr.qr_code
                                      }
                                      alt={`QR for ${qr.office_name}`}
                                      style={
                                        styles.tableQRImage
                                      }
                                    />
                                  ) : (
                                    <div
                                      style={
                                        styles.noQRImageSmall
                                      }
                                    >
                                      No QR
                                    </div>
                                  )}

                                  <span
                                    style={
                                      styles.qrIdBadge
                                    }
                                  >
                                    {qr.id}
                                  </span>

                                </div>

                              </td>

                              <td
                                style={
                                  styles.historyTd
                                }
                              >

                                <div
                                  style={
                                    styles.officeCell
                                  }
                                >
                                  <div
                                    style={
                                      styles.officeName
                                    }
                                  >
                                    {qr.office_name ||
                                      'Unnamed QR'}
                                  </div>

                                  <div
                                    style={
                                      styles.officeSubText
                                    }
                                  >
                                    QR Feedback
                                  </div>
                                </div>

                              </td>

                              <td
                                style={
                                  styles.historyTd
                                }
                              >
                                <div
                                  style={
                                    styles.dateCell
                                  }
                                >
                                  <span>
                                    📅
                                  </span>

                                  <span>
                                    {qr.created_at
                                      ? new Date(
                                          qr.created_at
                                        ).toLocaleDateString(
                                          'en-IN',
                                          {
                                            day: '2-digit',
                                            month:
                                              'short',
                                            year:
                                              'numeric'
                                          }
                                        )
                                      : '-'}
                                  </span>
                                </div>

                                {qr.created_at && (
                                  <div
                                    style={
                                      styles.timeText
                                    }
                                  >
                                    {new Date(
                                      qr.created_at
                                    ).toLocaleTimeString(
                                      'en-IN',
                                      {
                                        hour:
                                          '2-digit',
                                        minute:
                                          '2-digit'
                                      }
                                    )}
                                  </div>
                                )}
                              </td>

                              <td
                                style={
                                  styles.historyTd
                                }
                              >
                                <div
                                  style={
                                    styles.creatorCell
                                  }
                                >
                                  <span
                                    style={
                                      styles.creatorIcon
                                    }
                                  >
                                    👤
                                  </span>

                                  <span>
                                    {qr.username ||
                                      'Admin'}
                                  </span>
                                </div>
                              </td>

                              <td
                                style={
                                  styles.historyTd
                                }
                              >

                                {qr.fields &&
                                qr.fields.length >
                                  0 ? (
                                  <div
                                    style={
                                      styles.fieldsCell
                                    }
                                  >

                                    <span
                                      style={
                                        styles.fieldCountBadge
                                      }
                                    >
                                      {
                                        qr.fields
                                          .length
                                      }{' '}
                                      field
                                      {qr.fields
                                        .length !==
                                      1
                                        ? 's'
                                        : ''}
                                    </span>

                                    <div
                                      style={
                                        styles.fieldMiniList
                                      }
                                    >
                                      {qr.fields
                                        .slice(
                                          0,
                                          2
                                        )
                                        .map(
                                          (
                                            field,
                                            idx
                                          ) => (
                                            <span
                                              key={
                                                idx
                                              }
                                              style={
                                                styles.fieldMiniTag
                                              }
                                            >
                                              {field.field_label ||
                                                field.label}
                                              {field.is_required
                                                ? ' *'
                                                : ''}
                                              {(field.type === 'radio' || field.type === 'checkbox' || field.type === 'select') && field.options && (
                                                <span style={styles.optionCountMini}>
                                                  ({field.options.length} opts)
                                                </span>
                                              )}
                                            </span>
                                          )
                                        )}

                                      {qr.fields
                                        .length >
                                        2 && (
                                        <span
                                          style={
                                            styles.moreFields
                                          }
                                        >
                                          +
                                          {qr
                                            .fields
                                            .length -
                                            2}{' '}
                                          more
                                        </span>
                                      )}
                                    </div>

                                  </div>
                                ) : (
                                  <span
                                    style={
                                      styles.noFields
                                    }
                                  >
                                    No form
                                  </span>
                                )}

                              </td>

                              <td
                                style={
                                  styles.historyTd
                                }
                              >

                                <button
                                  onClick={() =>
                                    viewSubmissions(
                                      qr
                                    )
                                  }
                                  style={{
                                    ...styles.submissionCountBadge,
                                    ...(submissions ===
                                    0
                                      ? styles.zeroSubmissionBadge
                                      : {})
                                  }}
                                >
                                  📊 {submissions}
                                </button>

                              </td>

                              <td
                                style={
                                  styles.historyTd
                                }
                              >

                                <div
                                  style={
                                    styles.rowActions
                                  }
                                >

                                  <button
                                    onClick={() =>
                                      viewQRDetails(
                                        qr
                                      )
                                    }
                                    style={
                                      styles.viewBtn
                                    }
                                    title="View QR details"
                                  >
                                    👁️ View
                                  </button>

                                  <button
                                    onClick={() =>
                                      downloadQR(
                                        qr.qr_code,
                                        qr.id
                                      )
                                    }
                                    style={
                                      styles.downloadBtn
                                    }
                                    title="Download QR"
                                  >
                                    ⬇️ QR
                                  </button>

                                  <button
                                    onClick={() =>
                                      deleteQR(
                                        qr.id
                                      )
                                    }
                                    style={
                                      styles.deleteBtn
                                    }
                                    title="Delete QR"
                                  >
                                    🗑️
                                  </button>

                                </div>

                              </td>

                            </tr>
                          );
                        }
                      )}

                    </tbody>

                  </table>

                </div>
              )}

            </div>
          </div>
        )}

        {/* ======================================
            SUBMISSIONS TAB - Same as before
        ====================================== */}
        {activeTab === 'submissions' && (
          <div style={styles.tabContent}>

            <div style={styles.historyCard}>

              <div
                style={
                  styles.historyHeader
                }
              >

                <div>
                  <h2
                    style={
                      styles.sectionTitle
                    }
                  >
                    📊 All Submissions
                  </h2>

                  <p
                    style={
                      styles.historySubtitle
                    }
                  >
                    Select a QR to view its
                    submitted data
                  </p>
                </div>

                <span
                  style={
                    styles.totalBadge
                  }
                >
                  Total: {totalSubmissions}{' '}
                  submissions
                </span>

              </div>

              {/* SEARCH */}
              <div
                style={
                  styles.searchContainer
                }
              >
                <div
                  style={
                    styles.searchWrapper
                  }
                >

                  <span
                    style={
                      styles.searchIcon
                    }
                  >
                    🔍
                  </span>

                  <input
                    type="text"
                    placeholder="Search QR by name or ID..."
                    value={
                      submissionsSearchTerm
                    }
                    onChange={(e) =>
                      setSubmissionsSearchTerm(
                        e.target.value
                      )
                    }
                    style={
                      styles.searchInput
                    }
                  />

                  {submissionsSearchTerm && (
                    <button
                      onClick={
                        clearSubmissionsSearch
                      }
                      style={
                        styles.clearSearchBtn
                      }
                    >
                      ✕
                    </button>
                  )}

                </div>
              </div>

              {loading ? (
                <div
                  style={
                    styles.loadingContainer
                  }
                >
                  <div
                    style={
                      styles.spinner
                    }
                  />

                  <p
                    style={
                      styles.loadingText
                    }
                  >
                    Loading QR Codes...
                  </p>
                </div>
              ) : filteredSubmissionsQRs.length ===
                0 ? (
                <div
                  style={
                    styles.emptyState
                  }
                >
                  <div
                    style={
                      styles.emptyIcon
                    }
                  >
                    📭
                  </div>

                  <p
                    style={
                      styles.emptyMessage
                    }
                  >
                    {submissionsSearchTerm
                      ? 'No matching QR codes found.'
                      : 'No QR codes generated yet.'}
                  </p>

                  <p
                    style={
                      styles.emptySubMessage
                    }
                  >
                    Generate a QR code to
                    start collecting feedback.
                  </p>
                </div>
              ) : (
                <div
                  style={
                    styles.submissionsTableWrap
                  }
                >

                  <table
                    style={
                      styles.submissionsTable
                    }
                  >

                    <thead>
                      <tr>

                        <th
                          style={
                            styles.submissionsTh
                          }
                        >
                          #
                        </th>

                        <th
                          style={
                            styles.submissionsTh
                          }
                        >
                          QR ID
                        </th>

                        <th
                          style={
                            styles.submissionsTh
                          }
                        >
                          Office / QR Name
                        </th>

                        <th
                          style={
                            styles.submissionsTh
                          }
                        >
                          Created Date
                        </th>

                        <th
                          style={
                            styles.submissionsTh
                          }
                        >
                          Created By
                        </th>

                        <th
                          style={
                            styles.submissionsTh
                          }
                        >
                          Submissions
                        </th>

                        <th
                          style={
                            styles.submissionsTh
                          }
                        >
                          Action
                        </th>

                      </tr>
                    </thead>

                    <tbody>

                      {filteredSubmissionsQRs.map(
                        (qr, index) => {

                          const count =
                            Number(
                              qr.total_submissions
                            ) || 0;

                          return (
                            <tr
                              key={qr.id}
                              style={
                                styles.submissionsTr
                              }
                            >

                              <td
                                style={
                                  styles.submissionsTd
                                }
                              >
                                {index + 1}
                              </td>

                              <td
                                style={
                                  styles.submissionsTd
                                }
                              >
                                <span
                                  style={
                                    styles.qrIdBadge
                                  }
                                >
                                  {qr.id}
                                </span>
                              </td>

                              <td
                                style={
                                  styles.submissionsTd
                                }
                              >
                                <div
                                  style={
                                    styles.qrOfficeName
                                  }
                                >
                                  {qr.office_name ||
                                    'Unnamed QR'}
                                </div>

                                {qr.fields &&
                                  qr.fields
                                    .length >
                                    0 && (
                                    <div
                                      style={
                                        styles.qrFieldCount
                                      }
                                    >
                                      {
                                        qr.fields
                                          .length
                                      }{' '}
                                      form field
                                      {qr.fields
                                        .length !==
                                      1
                                        ? 's'
                                        : ''}
                                    </div>
                                  )}
                              </td>

                              <td
                                style={
                                  styles.submissionsTd
                                }
                              >
                                {qr.created_at
                                  ? new Date(
                                      qr.created_at
                                    ).toLocaleDateString(
                                      'en-IN',
                                      {
                                        day: '2-digit',
                                        month:
                                          'short',
                                        year:
                                          'numeric'
                                      }
                                    )
                                  : '-'}
                              </td>

                              <td
                                style={
                                  styles.submissionsTd
                                }
                              >
                                {qr.username ||
                                  'Admin'}
                              </td>

                              <td
                                style={
                                  styles.submissionsTd
                                }
                              >
                                <span
                                  style={{
                                    ...styles.submissionCountBadge,
                                    ...(count ===
                                    0
                                      ? styles.zeroSubmissionBadge
                                      : {})
                                  }}
                                >
                                  {count}
                                </span>
                              </td>

                              <td
                                style={
                                  styles.submissionsTd
                                }
                              >
                                <button
                                  onClick={() =>
                                    viewSubmissions(
                                      qr
                                    )
                                  }
                                  style={
                                    styles.viewSubmissionsRowBtn
                                  }
                                >
                                  View Submissions →
                                </button>
                              </td>

                            </tr>
                          );
                        }
                      )}

                    </tbody>

                  </table>

                </div>
              )}

            </div>
          </div>
        )}

        {/* ======================================
            QR DETAILS MODAL - Updated
        ====================================== */}
        {showQRModal && selectedQR && (
          <div
            style={
              styles.modalOverlay
            }
            onClick={() =>
              setShowQRModal(false)
            }
          >

            <div
              style={
                styles.modalContent
              }
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <button
                style={
                  styles.closeBtn
                }
                onClick={() =>
                  setShowQRModal(false)
                }
              >
                ×
              </button>

              <h3
                style={
                  styles.modalTitle
                }
              >
                QR Code Details
              </h3>

              <div
                style={
                  styles.modalQR
                }
              >
                {selectedQR.qr_code && (
                  <img
                    src={
                      selectedQR.qr_code
                    }
                    alt="QR Code"
                    style={
                      styles.modalQRImage
                    }
                  />
                )}
              </div>

              <div
                style={
                  styles.modalInfo
                }
              >

                <p>
                  <strong>
                    QR Number:
                  </strong>{' '}
                  {selectedQR.id}
                </p>

                <p>
                  <strong>
                    Office:
                  </strong>{' '}
                  {selectedQR.office_name}
                </p>

                <p>
                  <strong>
                    Date:
                  </strong>{' '}
                  {new Date(
                    selectedQR.created_at
                  ).toLocaleString()}
                </p>

                <p>
                  <strong>
                    Created By:
                  </strong>{' '}
                  {selectedQR.username ||
                    'Admin'}
                </p>

                <p>
                  <strong>
                    Submissions:
                  </strong>{' '}
                  {selectedQR.total_submissions ||
                    0}
                </p>

                {selectedQR.fields &&
                  selectedQR.fields.length >
                    0 && (
                    <div
                      style={
                        styles.fieldSection
                      }
                    >

                      <strong
                        style={
                          styles.fieldSectionTitle
                        }
                      >
                        Form Fields:
                      </strong>

                      <div
                        style={
                          styles.fieldTagsContainer
                        }
                      >
                        {selectedQR.fields.map(
                          (
                            field,
                            idx
                          ) => (
                            <span
                              key={idx}
                              style={
                                styles.fieldTag
                              }
                            >
                              {field.type === 'radio' ? '🔘 ' : 
                               field.type === 'checkbox' ? '☑️ ' : 
                               field.type === 'select' ? '📋 ' : ''}
                              {field.field_label ||
                                field.label}

                              {field.is_required
                                ? ' *'
                                : ''}
                              {(field.type === 'radio' || field.type === 'checkbox' || field.type === 'select') && field.options && (
                                <span style={styles.optionTagSmall}>
                                  ({field.options.length} opts)
                                </span>
                              )}
                            </span>
                          )
                        )}
                      </div>

                    </div>
                  )}

              </div>

              <div
                style={
                  styles.modalActions
                }
              >

                <button
                  onClick={() => {
                    setShowQRModal(false);
                    viewSubmissions(
                      selectedQR
                    );
                  }}
                  style={
                    styles.submissionsBtn
                  }
                >
                  📊 View Submissions
                </button>

                <button
                  onClick={() =>
                    downloadQR(
                      selectedQR.qr_code,
                      selectedQR.id
                    )
                  }
                  style={
                    styles.downloadBtn
                  }
                >
                  ⬇️ Download QR
                </button>

                <button
                  onClick={() =>
                    deleteQR(
                      selectedQR.id
                    )
                  }
                  style={
                    styles.deleteBtn
                  }
                >
                  🗑️ Delete
                </button>

              </div>

            </div>
          </div>
        )}

        {/* ======================================
            PRINT MODAL - Same as before
        ====================================== */}
        {showPrintModal && (
          <div
            style={
              styles.modalOverlay
            }
          >

            <div
              style={
                styles.modalContent
              }
            >

              <h3
                style={
                  styles.modalTitle
                }
              >
                Print QR Code
              </h3>

              <div
                id="qr-print-area"
                style={
                  styles.printArea
                }
              >

                <div
                  style={
                    styles.printHeader
                  }
                >
                  <h2
                    style={
                      styles.printTitle
                    }
                  >
                    Office QR Code
                  </h2>
                </div>

                <div
                  style={
                    styles.printQRContainer
                  }
                >
                  {qrCodeUrl && (
                    <img
                      src={qrCodeUrl}
                      alt="QR Code"
                      style={
                        styles.printQR
                      }
                    />
                  )}
                </div>

                <div
                  style={
                    styles.printInfo
                  }
                >
                  <p>
                    <strong>
                      Office:
                    </strong>{' '}
                    {officeName}
                  </p>

                  <p>
                    <strong>
                      Date:
                    </strong>{' '}
                    {new Date().toLocaleString()}
                  </p>

                  <p>
                    <strong>
                      Generated by:
                    </strong>{' '}
                    {user?.username ||
                      'Admin'}
                  </p>
                </div>

                <div
                  style={
                    styles.printFooter
                  }
                >
                  <p>
                    Scan this QR code to
                    submit feedback
                  </p>
                </div>

              </div>

              <div
                style={
                  styles.modalActions
                }
              >

                <button
                  onClick={printQR}
                  style={
                    styles.printBtn
                  }
                >
                  🖨️ Print / Save PDF
                </button>

                <button
                  onClick={() => {
                    setShowPrintModal(
                      false
                    );
                    setOfficeName('');
                    setQrCodeUrl('');
                    setFormFields([]);
                  }}
                  style={
                    styles.cancelBtn
                  }
                >
                  Cancel
                </button>

              </div>

            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// ============================================
// STYLES
// ============================================
const styles = {

  container: {
    minHeight: '100vh',
    background: '#f0f2f5'
  },

  header: {
    background:
      'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: 'white',
    padding: '15px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px'
  },

  headerTitle: {
    margin: 0,
    fontSize: '22px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },

  headerIcon: {
    fontSize: '24px'
  },

  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    flexWrap: 'wrap'
  },

  userInfo: {
    fontSize: '14px',
    color: '#e0e0e0',
    padding: '6px 12px',
    background:
      'rgba(255,255,255,0.1)',
    borderRadius: '6px'
  },

  logoutBtn: {
    padding: '8px 20px',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600'
  },

  content: {
    padding: '30px',
    maxWidth: '1500px',
    margin: '0 auto'
  },

  tabContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '30px',
    background: 'white',
    padding: '10px',
    borderRadius: '10px',
    boxShadow:
      '0 2px 10px rgba(0,0,0,0.1)'
  },

  tabButton: {
    padding: '12px 30px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    background: 'transparent',
    color: '#666'
  },

  activeTab: {
    background: '#667eea',
    color: 'white'
  },

  tabContent: {
    animation:
      'fadeIn 0.3s'
  },

  generateCard: {
    background: 'white',
    padding: '40px',
    borderRadius: '10px',
    boxShadow:
      '0 2px 10px rgba(0,0,0,0.1)',
    maxWidth: '700px',
    margin: '0 auto'
  },

  sectionTitle: {
    margin: 0,
    color: '#333',
    fontSize: '22px'
  },

  historySubtitle: {
    margin:
      '6px 0 0 0',
    color: '#888',
    fontSize: '13px'
  },

  inputGroup: {
    marginBottom: '20px'
  },

  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '600',
    color: '#333',
    fontSize: '14px'
  },

  input: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '16px',
    boxSizing: 'border-box'
  },

  select: {
    width: '100%',
    padding: '12px 14px',
    border: '2px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '16px',
    background: 'white'
  },

  checkbox: {
    marginRight: '8px'
  },

  formBuilderBtn: {
    width: '100%',
    padding: '12px',
    marginBottom: '20px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  formBuilderActive: {
    background: '#28a745'
  },

  formBuilder: {
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
    background: '#f8f9fa'
  },

  formBuilderTitle: {
    marginTop: 0,
    marginBottom: '20px',
    color: '#333'
  },

  addFieldForm: {
    background: 'white',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px'
  },

  fieldRow: {
    display: 'grid',
    gridTemplateColumns:
      '1fr 1fr',
    gap: '15px',
    marginBottom: '15px'
  },

  fieldGroup: {
    display: 'flex',
    flexDirection: 'column'
  },

  addFieldBtn: {
    padding: '10px 20px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    marginTop: 'auto'
  },

  fieldsList: {
    marginTop: '20px'
  },

  fieldsTitle: {
    marginBottom: '10px',
    color: '#333'
  },

  fieldItem: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    padding: '10px 15px',
    background: 'white',
    borderRadius: '6px',
    marginBottom: '8px',
    border:
      '1px solid #e0e0e0'
  },

  fieldItemInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },

  fieldTypeBadge: {
    background: '#667eea',
    color: 'white',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '12px'
  },

  fieldLabelText: {
    fontWeight: '600',
    color: '#333'
  },

  requiredBadge: {
    background: '#dc3545',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '10px'
  },

  optionsBadge: {
    background: '#17a2b8',
    color: 'white',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '10px'
  },

  fieldItemActions: {
    display: 'flex',
    gap: '5px'
  },

  moveBtn: {
    padding: '4px 8px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },

  editBtn: {
    padding: '4px 8px',
    background: '#ffc107',
    color: '#333',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },

  removeFieldBtn: {
    padding: '4px 8px',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  },

  previewSection: {
    marginTop: '20px',
    padding: '20px',
    background: 'white',
    borderRadius: '8px'
  },

  previewTitle: {
    marginTop: 0,
    marginBottom: '15px',
    color: '#333'
  },

  previewForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },

  previewField: {
    display: 'flex',
    flexDirection: 'column'
  },

  previewLabel: {
    fontWeight: '600',
    marginBottom: '5px',
    color: '#333'
  },

  requiredStar: {
    color: '#dc3545',
    marginLeft: '4px'
  },

  previewInput: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    boxSizing: 'border-box'
  },

  previewSelect: {
    padding: '8px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    width: '100%',
    background: 'white',
    boxSizing: 'border-box'
  },

  ratingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },

  starContainer: {
    display: 'flex',
    gap: '5px'
  },

  // Options styles
  optionsContainer: {
    marginTop: '15px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginBottom: '15px'
  },

  optionsInputRow: {
    display: 'flex',
    gap: '10px',
    marginBottom: '10px'
  },

  addOptionBtn: {
    padding: '10px 20px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },

  optionsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    minHeight: '30px'
  },

  optionTag: {
    background: '#e9ecef',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: '1px solid #dee2e6'
  },

  removeOptionBtn: {
    background: 'none',
    border: 'none',
    color: '#dc3545',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    padding: '0 4px'
  },

  noOptionsText: {
    color: '#999',
    fontSize: '13px',
    fontStyle: 'italic'
  },

  optionsHint: {
    marginTop: '8px',
    fontSize: '12px',
    color: '#28a745',
    fontWeight: '600'
  },

  warningText: {
    fontSize: '12px',
    color: '#dc3545',
    marginTop: '4px'
  },

  // Preview styles for radio, checkbox, select
  radioGroupPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px 0'
  },

  radioLabelPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  radioInputPreview: {
    width: '16px',
    height: '16px',
    cursor: 'pointer'
  },

  checkboxGroupPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '8px 0'
  },

  checkboxLabelPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  checkboxInputPreview: {
    width: '16px',
    height: '16px',
    cursor: 'pointer'
  },

  optionCountMini: {
    fontSize: '9px',
    color: '#888',
    marginLeft: '4px'
  },

  optionTagSmall: {
    fontSize: '9px',
    color: '#888',
    marginLeft: '4px'
  },

  // ==========================================
  // HISTORY
  // ==========================================

  historyCard: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    boxShadow:
      '0 2px 12px rgba(0,0,0,0.08)'
  },

  historyHeader: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    marginBottom: '25px',
    flexWrap: 'wrap',
    gap: '10px'
  },

  totalBadge: {
    background: '#667eea',
    color: 'white',
    padding: '7px 16px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600'
  },

  searchContainer: {
    marginBottom: '25px'
  },

  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    background: '#f8f9fa',
    border:
      '2px solid #e0e0e0',
    borderRadius: '8px',
    padding: '0 15px',
    position: 'relative'
  },

  searchIcon: {
    fontSize: '18px',
    marginRight: '10px'
  },

  searchInput: {
    flex: 1,
    padding: '12px 0',
    border: 'none',
    background: 'transparent',
    fontSize: '16px',
    outline: 'none',
    color: '#333'
  },

  clearSearchBtn: {
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: '18px',
    cursor: 'pointer'
  },

  // ==========================================
  // HISTORY TABLE
  // ==========================================

  historyTableWrap: {
    width: '100%',
    overflowX: 'auto',
    border:
      '1px solid #e6e9ef',
    borderRadius: '10px'
  },

  historyTable: {
    width: '100%',
    minWidth: '1150px',
    borderCollapse: 'collapse',
    background: 'white'
  },

  historyTh: {
    background:
      'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: 'white',
    padding: '14px 12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    whiteSpace: 'nowrap',
    borderBottom:
      '2px solid #667eea'
  },

  historyTr: {
    borderBottom:
      '1px solid #edf0f4',
    transition:
      'background 0.2s ease'
  },

  historyTd: {
    padding: '12px',
    verticalAlign: 'middle',
    color: '#333',
    fontSize: '13px',
    background: '#fff'
  },

  rowNumber: {
    width: '28px',
    height: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f0f2f5',
    borderRadius: '50%',
    fontWeight: '700',
    color: '#555'
  },

  qrCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px'
  },

  tableQRImage: {
    width: '70px',
    height: '70px',
    objectFit: 'contain',
    padding: '4px',
    background: 'white',
    border:
      '1px solid #e2e5ea',
    borderRadius: '6px'
  },

  noQRImageSmall: {
    width: '70px',
    height: '70px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f5f5f5',
    color: '#999',
    fontSize: '10px',
    borderRadius: '6px'
  },

  qrIdBadge: {
    display: 'inline-block',
    background: '#1a1a2e',
    color: '#FFD700',
    padding: '4px 9px',
    borderRadius: '5px',
    fontSize: '11px',
    fontWeight: '700',
    whiteSpace: 'nowrap'
  },

  officeCell: {
    minWidth: '150px'
  },

  officeName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: '4px'
  },

  officeSubText: {
    fontSize: '11px',
    color: '#999'
  },

  dateCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap'
  },

  timeText: {
    marginTop: '4px',
    marginLeft: '22px',
    color: '#999',
    fontSize: '11px'
  },

  creatorCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap'
  },

  creatorIcon: {
    fontSize: '13px'
  },

  fieldsCell: {
    minWidth: '130px'
  },

  fieldCountBadge: {
    display: 'inline-block',
    background: '#eef2ff',
    color: '#667eea',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '10px',
    fontWeight: '700',
    marginBottom: '5px'
  },

  fieldMiniList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  },

  fieldMiniTag: {
    fontSize: '10px',
    color: '#555',
    whiteSpace: 'nowrap',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  moreFields: {
    fontSize: '10px',
    color: '#667eea',
    fontWeight: '600'
  },

  noFields: {
    color: '#aaa',
    fontSize: '11px'
  },

  submissionCountBadge: {
    border: 'none',
    background: '#e8f7ed',
    color: '#198754',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },

  zeroSubmissionBadge: {
    background: '#f1f1f1',
    color: '#888'
  },

  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    flexWrap: 'nowrap'
  },

  viewBtn: {
    padding: '6px 10px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },

  downloadBtn: {
    padding: '6px 10px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },

  deleteBtn: {
    padding: '6px 10px',
    background: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600'
  },

  submissionsBtn: {
    padding: '8px 14px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600'
  },

  // ==========================================
  // LOADING
  // ==========================================

  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px'
  },

  spinner: {
    width: '40px',
    height: '40px',
    border:
      '4px solid #e0e0e0',
    borderTop:
      '4px solid #667eea',
    borderRadius: '50%',
    animation:
      'spin 1s linear infinite'
  },

  loadingText: {
    marginTop: '15px',
    color: '#666'
  },

  emptyState: {
    textAlign: 'center',
    padding: '60px 20px'
  },

  emptyIcon: {
    fontSize: '48px',
    marginBottom: '15px'
  },

  emptyMessage: {
    fontSize: '18px',
    color: '#333'
  },

  emptySubMessage: {
    fontSize: '14px',
    color: '#999'
  },

  // ==========================================
  // SUBMISSIONS TABLE
  // ==========================================

  submissionsTableWrap: {
    width: '100%',
    overflowX: 'auto',
    border:
      '1px solid #e6e9ef',
    borderRadius: '10px'
  },

  submissionsTable: {
    width: '100%',
    minWidth: '900px',
    borderCollapse: 'collapse'
  },

  submissionsTh: {
    background:
      'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: 'white',
    padding: '13px',
    textAlign: 'left',
    fontSize: '12px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap'
  },

  submissionsTr: {
    borderBottom:
      '1px solid #edf0f4'
  },

  submissionsTd: {
    padding: '13px',
    fontSize: '13px',
    color: '#333'
  },

  qrOfficeName: {
    fontWeight: '700',
    color: '#1a1a2e'
  },

  qrFieldCount: {
    fontSize: '11px',
    color: '#999',
    marginTop: '4px'
  },

  viewSubmissionsRowBtn: {
    padding: '7px 12px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600',
    whiteSpace: 'nowrap'
  },

  // ==========================================
  // MODAL
  // ==========================================

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'rgba(0,0,0,0.55)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px'
  },

  modalContent: {
    background: 'white',
    padding: '30px',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative'
  },

  closeBtn: {
    position: 'absolute',
    top: '10px',
    right: '15px',
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#999'
  },

  modalTitle: {
    textAlign: 'center',
    marginBottom: '20px',
    color: '#333',
    fontSize: '22px'
  },

  modalQR: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px'
  },

  modalQRImage: {
    width: '200px',
    height: '200px',
    objectFit: 'contain',
    border:
      '2px solid #e0e0e0',
    borderRadius: '8px',
    padding: '10px'
  },

  modalInfo: {
    marginBottom: '20px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '6px'
  },

  modalActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },

  fieldSection: {
    marginTop: '15px',
    paddingTop: '15px',
    borderTop:
      '1px solid #e0e0e0'
  },

  fieldSectionTitle: {
    display: 'block',
    marginBottom: '10px',
    fontSize: '14px'
  },

  fieldTagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },

  fieldTag: {
    background: '#f0f4ff',
    color: '#667eea',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px'
  },

  // ==========================================
  // PRINT
  // ==========================================

  printArea: {
    padding: '20px',
    background: 'white',
    textAlign: 'center',
    marginBottom: '20px',
    border:
      '1px solid #e0e0e0',
    borderRadius: '6px'
  },

  printHeader: {
    marginBottom: '15px'
  },

  printTitle: {
    margin: 0,
    color: '#1a1a2e',
    fontSize: '24px'
  },

  printQRContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '15px'
  },

  printQR: {
    width: '200px',
    height: '200px',
    objectFit: 'contain'
  },

  printInfo: {
    textAlign: 'left',
    padding: '10px',
    background: '#f8f9fa',
    borderRadius: '6px',
    marginBottom: '10px'
  },

  printFooter: {
    fontSize: '12px',
    color: '#666',
    borderTop:
      '1px solid #e0e0e0',
    paddingTop: '10px'
  },

  printBtn: {
    padding: '10px 20px',
    background: '#17a2b8',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600'
  },

  cancelBtn: {
    padding: '10px 20px',
    background: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: '600'
  },

  generateBtn: {
    width: '100%',
    padding: '14px',
    background: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  buttonDisabled: {
    background: '#6c757d',
    cursor: 'not-allowed'
  }
};

// ============================================
// ANIMATIONS
// ============================================
const styleSheet =
  document.createElement('style');

styleSheet.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    .qr-history-table {
      min-width: 1100px;
    }
  }
`;

document.head.appendChild(
  styleSheet
);

export default Admin;